import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { AppointmentDetails, AppointmentDetailsProps } from "@/utils/types";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import Spinner from "@/components/loader/Spinner";

const AppointmentDetailsCard: React.FC<AppointmentDetailsProps> = ({
  isOpen,
  onClose,
  appointment_id,
  branchTimezone,
  onStatusChange,
}) => {
  const initialAppointment: AppointmentDetails = {
    id: 0,
    customer_name: "",
    phone_number: "",
    appointment_time: "",
    duration: "",
    status: "",
    service: "",
    created_at: "",
    employee: "",
    employee_name: "",
    order_name: "",
    order_fulfillment_status: "",
    order_payment_status: "",
    selected_options: [],
    order_date: "",
    order_charges_details: {
      subtotal: "",
      discount: "",
      tax: "",
      tips: "",
      service_fee: "",
      total: "",
    },
    service_charge: "",
    appointment_source: "",
    notes: "",
  };

  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [appointment, setAppointment] =
    useState<AppointmentDetails>(initialAppointment);
  const [isAppointmentLoading, setIsAppointmentLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(
    appointment.status === "" ? "Scheduled" : appointment.status,
  );

  useEffect(() => {
    const fetchAppointment = async () => {
      if (!axiosInstance) return;

      try {
        setIsAppointmentLoading(true);
        const response = await axiosInstance.get(
          `api/v1/appointment/${appointment_id}/`,
        );
        console.log(response.data);
        setAppointment(response.data);
        setSelectedStatus(response.data.status);
      } catch (error) {
        console.error("Error fetching appointment:", error);
        toast.error("Failed to load appointment details");
      } finally {
        setIsAppointmentLoading(false);
      }
    };

    if (isOpen && appointment_id) {
      fetchAppointment();
    }
  }, [appointment_id, axiosInstance, isOpen]);

  const { t } = useTranslation();

  const dateToBranchTimezone = (date: Date) => {
    const zonedDate = toZonedTime(date, branchTimezone);
    return zonedDate;
  };

  const separateDateAndTime = (date: Date) => {
    const dateString = format(date, "MM/dd/yyyy");
    const timeString = format(date, "hh:mm a");
    return { date: dateString, time: timeString };
  };

  const formatDateAndTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const dateInBranchTimezone = dateToBranchTimezone(date);
      return separateDateAndTime(dateInBranchTimezone);
    } catch (e) {
      return { date: "N/A", time: "N/A" };
    }
  };

  const { date, time } = formatDateAndTime(appointment.appointment_time);

  const { date: orderDate, time: orderTime } = formatDateAndTime(
    appointment.order_date,
  );

  const formatCurrency = (value: string | number): string => {
    if (value === null || value === undefined || value === "") return "0.00";
    const numValue =
      typeof value === "string" ? parseFloat(value || "0") : value;
    return numValue.toFixed(2);
  };

  const handleStatusChangeAndClose = () => {
    onStatusChange(selectedStatus);
    onClose();
  };

  if (!isOpen) return null;

  if (loading || isFetching || isAppointmentLoading)
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div
          className="fixed inset-0 bg-black bg-opacity-50"
          onClick={onClose}
        />
        <div className="relative z-10 w-full max-w-xl overflow-hidden">
          <div className="p-4">
            <div className="flex items-center justify-center">
              <Spinner />
            </div>
          </div>
        </div>
      </div>
    );

  if (appointment.appointment_source === "Google Calendar") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div
          className="fixed inset-0 bg-black bg-opacity-50"
          onClick={onClose}
        />
        <div className="relative z-10 w-full max-w-xl overflow-hidden rounded-lg bg-white shadow-xl">
          <div className="p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {t("back.management.appointment.details")} -{" "}
                {appointment.appointment_source}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="mt-4">
              <p className="text-gray-600">{appointment.notes}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-xl overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">
              {t("back.management.appointment.details")}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="mt-4 max-h-80 overflow-y-auto rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between border-b-2 border-dotted border-gray-300 pb-3">
              <h3 className="text-xl font-bold uppercase">
                {t("back.management.appointment.appointment")}
              </h3>
            </div>

            <div className="mt-4 border-b-2 border-dotted border-gray-300 pb-3">
              <span className="text-base font-bold">
                {t("back.management.appointment.date")}:{" "}
              </span>
              <span className="text-base font-normal">
                {date}, {time}
              </span>
            </div>

            <div className="flex justify-between border-b-2 border-dotted border-gray-300 py-3">
              <div className="flex items-center">
                <span className="text-base font-bold">
                  {t("back.management.appointment.employee")}:
                </span>
                <span className="ml-2 text-base font-normal">
                  {appointment.employee_name || "N/A"}
                </span>
              </div>
              <div className="text-base">
                <span className="font-bold">
                  {t("back.management.appointment.payment_status")}:
                </span>{" "}
                <span
                  className={`font-normal ${
                    appointment.order_payment_status === "Paid"
                      ? "rounded-xl bg-green-100 px-2 py-1 font-bold text-green-700"
                      : "rounded-xl bg-yellow-100 px-2 py-1 font-bold text-yellow-700"
                  }`}
                >
                  {appointment.order_payment_status === "PostPaid"
                    ? t("back.management.appointment.unpaid")
                    : appointment.order_payment_status}
                </span>
              </div>
            </div>

            <div className="mt-2 border-b-2 border-dotted border-gray-300 pb-3">
              <div className="mt-3">
                <span className="text-base font-bold">
                  {t("back.management.appointment.customer")}:{" "}
                </span>
                <span className="text-base font-normal">
                  {appointment.customer_name}
                </span>
              </div>

              <div className="mt-1">
                <span className="text-base font-bold">
                  {t("back.management.appointment.contact")}:{" "}
                </span>
                <span className="text-base font-normal">
                  {appointment.phone_number}
                </span>
              </div>

              <div className="mt-1">
                <span className="text-base font-bold">
                  {t("back.management.appointment.order_id")}:{" "}
                </span>
                <span className="text-base font-normal">
                  {appointment.order_name}
                </span>
              </div>

              <div className="mt-1">
                <span className="text-base font-bold">
                  {appointment.order_payment_status === "Paid"
                    ? t("back.management.appointment.paid")
                    : t("back.management.appointment.unpaid")}
                  :{" "}
                </span>
                <span className="text-base font-normal">
                  {orderDate}, {orderTime}
                </span>
              </div>
            </div>

            <div className="flex justify-between border-b-2 border-dotted border-gray-300 py-3">
              <div className="flex w-full flex-col">
                <div className="flex w-full items-center justify-between">
                  <span className="ml-2 text-base font-bold">
                    {appointment.service}
                  </span>
                  <span className="ml-2 text-base font-bold">
                    ${formatCurrency(appointment.service_charge)}
                  </span>
                </div>
                {appointment.selected_options &&
                  appointment.selected_options.length > 0 && (
                    <div className="mt-1 flex w-full flex-col pl-4">
                      {appointment.selected_options.map((option) => (
                        <div
                          key={option.id}
                          className="flex items-center justify-between text-sm"
                        >
                          <span className="font-normal">
                            {option.name}{" "}
                            {option.quantity > 0 && `x ${option.quantity}`}
                          </span>
                          <span className="ml-2 text-gray-600">
                            +${formatCurrency(option.surcharge)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>

            <div className="mt-4 space-y-2 border-b-2 border-dotted border-gray-300 pb-3">
              <div className="flex justify-between">
                <span className="text-base text-gray-600">
                  {t("back.management.appointment.subtotal")}
                </span>
                <span className="text-base">
                  ${formatCurrency(appointment.order_charges_details?.subtotal)}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-base text-gray-600">
                  {t("back.management.appointment.tax")}
                </span>
                <span className="text-base">
                  ${formatCurrency(appointment.order_charges_details?.tax)}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-base text-gray-600">
                  {t("back.management.appointment.tips")}
                </span>
                <span className="text-base">
                  ${formatCurrency(appointment.order_charges_details?.tips)}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-base text-gray-600">
                  {t("back.management.appointment.serviceFee")}
                </span>
                <span className="text-base">
                  $
                  {formatCurrency(
                    appointment.order_charges_details?.service_fee,
                  )}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-base text-gray-600">
                  {t("back.management.appointment.discount")}
                </span>
                <span className="text-base">
                  -$
                  {formatCurrency(appointment.order_charges_details?.discount)}
                </span>
              </div>

              <div className="border-t border-gray-300 pt-2">
                <div className="flex justify-between">
                  <span className="text-base font-bold">
                    {t("back.management.appointment.total")}
                  </span>
                  <span className="text-base font-bold">
                    ${formatCurrency(appointment.order_charges_details?.total)}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-3 py-3 text-center">
              <span className="text-xl font-bold uppercase">
                {appointment.status === "Completed"
                  ? "Checked In"
                  : appointment.status}
              </span>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-base font-medium text-gray-700">
              {t("back.management.appointment.changeStatus")}
            </label>
            <select
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-base shadow-sm"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="Scheduled">
                {t("back.management.appointment.scheduled")}
              </option>
              <option value="Completed">
                {t("back.management.appointment.completed")}
              </option>
              <option value="Cancelled">
                {t("back.management.appointment.cancelled")}
              </option>
            </select>
          </div>

          <div className="mt-4 flex justify-end space-x-3">
            <button
              onClick={handleStatusChangeAndClose}
              className="rounded-md bg-red-500 px-4 py-2 text-base font-medium text-white hover:bg-red-600"
            >
              {t("back.management.appointment.changeStatus")}
            </button>
            <button
              onClick={onClose}
              className="rounded-md bg-gray-600 px-4 py-2 text-base font-medium text-white hover:bg-gray-700"
            >
              {t("back.management.appointment.close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentDetailsCard;
